import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useRef } from 'react';
import { useDragAndDrop } from '../../../_lib/DragAndDrop';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import { ContentWrapperDragPreview } from './partials/ContentWrapperDragPreview';
import { ContentWrapperBlocks } from './ContentWrapper.types';

type ContentWrapperDnd = {
  block: ContentWrapperBlocks;
  position: number;
  moveBlockToBlock(sourceId: string, targetId: string): void;
  submitNewBlockToBlockMove(blockUuid: string): void;
  createSnapshotOfBlockOrder(): void;
};

export type ContentWrapperDnDContext = {
  draggable: 'true';
  onDragStart(e: React.DragEvent<HTMLElement>): void;
  onDragEnter(): void;
  onDragEnd(): void;
};
export const ContentWrapperDndContext = createContext<ContentWrapperDnDContext | null>(null);
export const useContentWrapperDndContext = () => useContext(ContentWrapperDndContext);

/**
 * DnD Provider for all ContentWrapper blocks.
 * dndHtmlAttributes will be used in the corresponding blocks and passed to BlockInteractions.
 */
export function ContentWrapperDndProvider({
  block,
  position,
  moveBlockToBlock,
  submitNewBlockToBlockMove,
  createSnapshotOfBlockOrder,
  children,
}: PropsWithChildren<ContentWrapperDnd>) {
  const divRef = useRef<HTMLDivElement>(null);
  const { editModeInfo } = useAppContext();

  const { isDragging, onDragStart, onDragEnter, onDragOver, onDragEnd } = useDragAndDrop(
    block.uuid,
    (sourceId, targetId) => moveBlockToBlock(sourceId, targetId),
    () => <ContentWrapperDragPreview block={block} position={position} />,
    undefined,
    submitNewBlockToBlockMove,
  );

  useEffect(() => {
    const element = divRef.current;
    if (isDragging && element) {
      element.classList.add('content-wrapper__drag-to-preview--dragging');
      return () => {
        element.classList.remove('content-wrapper__drag-to-preview--dragging');
      };
    }
  }, [isDragging]);

  const dndHtmlAttributes = useMemo(
    () => ({
      draggable: 'true' as const,
      onDragStart: (e: React.DragEvent<HTMLElement>) => {
        onDragStart(e);
        createSnapshotOfBlockOrder();
      },
      onDragEnter,
      onDragEnd,
    }),
    [createSnapshotOfBlockOrder, onDragEnd, onDragEnter, onDragStart],
  );

  if (!editModeInfo.active) {
    return children;
  }

  return (
    <ContentWrapperDndContext.Provider value={dndHtmlAttributes}>
      {/* onDragOver needs to be calculated over the complete block here */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div ref={divRef} className="content-wrapper__drag-to-preview" onDragOver={onDragOver}>
        {children}
      </div>
    </ContentWrapperDndContext.Provider>
  );
}
